import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { RootStore } from 'store/root.store';
import * as Styled from './styled';
import { Check, Delete, PlayArrow, Save, Stop, Undo, CallMerge } from '@material-ui/icons';
import { Button, Checkbox, CircularProgress, Fab, TextField, Tooltip } from '@material-ui/core';
import { DateTime } from 'luxon';
import TimerStore from 'store/Timer/timer.store';
import TimerChunkItem from 'components/TimerChunkItem/TimerChunkItem';
import RunningTimerChunkItem from 'components/TimerChunkItem/RunningTimerChunkItem';
import { FlexDiv } from 'common/flex';
import { AutoSizer, Index, List } from 'react-virtualized';
import ImmutableTimer from '../../api/immutables/ImmutableTimer';
import AutoCompleteField from 'components/AutoCompleteField/AutoCompleteField';
import { MatterItemFormatter } from 'components/TemplateForm';
import { Features, Matter, TimeKeeperAssignment, TimerChunk, TimerChunksHeader } from '../../api/types/types';
import { ApiConsumer } from 'common/ApiProvider';
import RootAPI from '../../api/interfaces/RootAPI';
import Template from '../../api/immutables/ImmutableTemplate';
import { FabContainer } from '../Home/styled';
import { FeaturesConsumer } from 'common/FeaturesProvider';
import { TKConsumer } from 'common/TKProvider';
import { getTimerDurationTxt } from 'util/timer';
import { withTranslation } from 'react-i18next';
import { getDateFormat } from 'util/date';
import { handleEnterKey } from 'util/hotKeys';

interface HeaderTimerProps {
    startStopRef?: React.RefObject<HTMLElement>;
    setStartStopFocusable: (val: boolean) => void;
    timer: ImmutableTimer,
    onStart: () => void;
    onStop: () => void;
    loadingId?: number;
    includeSubmitted: boolean;
    // tslint:disable-next-line:no-any
    t: any;
}
interface HeaderTimerState {
    durTxt: string;
}

export class HeaderTimer extends React.Component<HeaderTimerProps, HeaderTimerState> {
    interval: NodeJS.Timeout;

    constructor(props: HeaderTimerProps) {
        super(props);
        this.state = { durTxt: '' };
        if (props.timer.active) {
            this.interval = setInterval(this.timerTick, 250);
        }
    }
    componentDidMount() {
        this.props.setStartStopFocusable(false);
    }
    componentWillUnmount() {
        clearInterval(this.interval);
    }
    getSnapshotBeforeUpdate(prevProps: HeaderTimerProps) {
        if (prevProps.timer.active === this.props.timer.active) {
            return null;
        }
        clearInterval(this.interval);
        if (this.props.timer.active) {
            this.interval = setInterval(this.timerTick, 250);
        }
        return null;
    }
    timerTick = () => {
        const { timer, includeSubmitted } = this.props;
        this.setState({ durTxt: getTimerDurationTxt(timer, includeSubmitted) });
    }
    render() {
        const {
            t,
            startStopRef,
            timer,
            loadingId,
            includeSubmitted,
            onStart,
            onStop,
            setStartStopFocusable
        } = this.props;
        const dtxt = timer.active ? this.state.durTxt : getTimerDurationTxt(timer, includeSubmitted);
        return (
            <TKConsumer>
                {(tk: TimeKeeperAssignment) =>
                    <Styled.TimerHeaderContainer>
                        <div style={{ float: 'left' }}>
                            <Tooltip title={t(timer.active ? 'action.pause' : 'action.start')}>
                                <Fab
                                    buttonRef={startStopRef}
                                    disabled={!!loadingId || !tk.writable}
                                    onClick={() => {
                                        setStartStopFocusable(true);
                                        timer.active ? onStop!() : onStart!();
                                    }}
                                    size="medium"
                                    color={timer.active ? undefined : 'primary'}
                                    style={timer.active ?
                                        { backgroundColor: 'rgb(255, 87, 34)', color: 'white' }
                                    :
                                        undefined
                                    }
                                    aria-label={t(timer.active ? 'action.pause' : 'action.start')}
                                >
                                    {timer.active ? <Stop /> : <PlayArrow />}
                                    {(loadingId === timer.id) && <Styled.FabProgress size={55} />}
                                </Fab>
                            </Tooltip>
                        </div>
                        <Styled.TimerHeaderText>
                            {dtxt}
                        </Styled.TimerHeaderText>
                    </Styled.TimerHeaderContainer>
                }
            </TKConsumer>

        );
    }
}

interface Props {
    fabRef: React.RefObject<HTMLElement>;
    fabButtonRef: React.RefObject<HTMLElement>;
    timerStore?: TimerStore;
    match: {
        params: {
            id: string;
        }
    };
    // tslint:disable-next-line:no-any
    t: any;
}

@inject((allStores: { rootStore: RootStore }) => {
    let rootStore = allStores.rootStore;
    return {
        timerStore: rootStore.timerStore
    };
})
@observer
class TimerFormContainer extends React.Component<Props> {
    refFocableElement = React.createRef<HTMLElement>();
    startStopFocusable: boolean = false;
    initialFocus: boolean = true;

    componentWillMount() {
        // this.props.templateStore!.resetValidation();
        if (this.props.match.params.id === 'new') {
            // this.props.templateStore!.newTemplate();
        } else {
            this.props.timerStore!.wrappedLoadTimer(Number(this.props.match.params.id));
        }
    }
    componentDidUpdate(prevProps: Props) {
        if (this.props.match.params.id !== prevProps.match.params.id) {
            this.props.timerStore!.wrappedLoadTimer(Number(this.props.match.params.id));
            this.initialFocus = true;
        }
    }
    setStartStopFocusable = (val: boolean) => {
        this.initialFocus = false;
        this.startStopFocusable = val;
        if (this.refFocableElement.current) {
            this.refFocableElement.current!.focus();
        }
    }
    fetchMatters = (api: RootAPI) => async (searchText: string, offset: number, limit: number, features: Features) => {
        let  results: Matter[] = await api.Matter.searchMatters(searchText, true, undefined, undefined, offset, limit);
        if (!features.EpochConfigTrackedMatterClientsEnabled && results.length === 0) {
            results = await api.Matter.getAvailableMatters(searchText, false, undefined, undefined, offset, limit);
        }
        return results;
    }

    render() {
        const { t, timerStore } = this.props;
        const {
            selectedTimer,
            sortedChunks,
            startTimer,
            loadingTimer,
            setMatter,
            deleteChunk,
            editChunk,
            toggleSelect,
            setTemplate,
            setName,
            matter,
            template,
            chunkEdit,
            stopTimer,
            dirty,
            selectedChunks,
            selectAllStatus,
            selectAllTimeSegments,
            timerLoading,
            openTimeEntry,
            saveTimer,
            revert,
            clearSelections,
            deleteSelectedChunks,
            entryFromSelectedChunks,
            openTransferDialog,
            validation,
            originalTimer,
            addTimerNote,
            updateSegmentNarrative,
            shouldUpdateChunkProps,
            setUpdateChunkProps,
            selectAllForDay,
            rootStore
        } = timerStore!;
        const includeSubmitted = rootStore.appStore.features.EpochConfigTimeSegmentsSubmittedVisible;
        if (!selectedTimer || timerLoading) {
            return (
                <div style={{textAlign: 'center'}}>
                    <CircularProgress/>
                </div>
            );
        }

        const fetchTemplates = rootStore.templateStore.fetchTemplatesInAutoComplate;
        const matterLabel = rootStore.appStore.features.EpochConfigMatterLabel;
        return (
            <TKConsumer>
                {(tk: TimeKeeperAssignment) => {
                    const checkboxDisabled = !tk.writable || sortedChunks.filter((c) => !('isHeader' in c) && !c.timeEntryId).length === 0;
                    return (
                        <>
                            <Styled.TimerHeader>
                                <FlexDiv>
                                    <Checkbox
                                        checked={selectAllStatus}
                                        onChange={selectAllTimeSegments}
                                        disabled={checkboxDisabled}
                                        inputRef={!checkboxDisabled && this.initialFocus ? this.refFocableElement : undefined}
                                        inputProps={{
                                            'aria-label': t('selected_timer.header.checkbox.aria_label')
                                        }}
                                    />
                                </FlexDiv>
                                <HeaderTimer
                                    startStopRef={(checkboxDisabled && this.initialFocus) || this.startStopFocusable ? this.refFocableElement : undefined}
                                    setStartStopFocusable={this.setStartStopFocusable}
                                    timer={selectedTimer}
                                    loadingId={loadingTimer}
                                    onStart={() => startTimer(originalTimer)}
                                    onStop={() => stopTimer(selectedTimer.id!)}
                                    includeSubmitted={includeSubmitted}
                                    t={t}
                                />
                                <FlexDiv flex={1}>
                                    <TextField
                                        style={{ width: '97%' }}
                                        value={selectedTimer.name}
                                        disabled={selectedTimer.active || !tk.writable}
                                        label={t('selected_timer.header.field.name.label')}
                                        onChange={(evt: React.ChangeEvent<HTMLInputElement>) => setName(evt.target.value)}
                                        fullWidth={true}
                                        error={!!validation.name}
                                        helperText={validation.name ? t(validation.name) : undefined}
                                        inputProps={{
                                            'aria-label': `
                                                ${t('selected_timer.header.field.name.aria-label')}:
                                                ${!!validation.name ? t('error', { ns: 'common', errorMessage: t(validation.name)}) : ''}
                                            `
                                        }}
                                    />
                                </FlexDiv>
                                <FeaturesConsumer>
                                    {(features: Features) =>
                                        <ApiConsumer>
                                            {(api: RootAPI) =>
                                                <>
                                                    <FlexDiv direction="column" flex={1}>
                                                        <AutoCompleteField
                                                            width="99%"
                                                            label={t('selected_timer.header.field.matter', { matterLabel })}
                                                            fetch={(...props) => this.fetchMatters(api)(...props, features)}
                                                            currentItem={matter}
                                                            clearable={true}
                                                            onClear={() => setMatter()}
                                                            formatItem={MatterItemFormatter}
                                                            getItemText={(m: Matter) => `${m.number} - ${m.name}`}
                                                            onSelect={setMatter}
                                                            disabled={selectedTimer.active || !tk.writable}
                                                            tooltip={(m: Matter) => m.description}
                                                        />
                                                    </FlexDiv>
                                                    <FlexDiv direction="column" flex={1}>
                                                        <AutoCompleteField
                                                            width="98%"
                                                            label={t('selected_timer.header.field.template')}
                                                            fetch={fetchTemplates}
                                                            currentItem={template}
                                                            clearable={true}
                                                            disabled={selectedTimer.active || !tk.writable}
                                                            onClear={() => setTemplate()}
                                                            getItemText={(m: Template) => `${m.name}`}
                                                            onSelect={setTemplate}
                                                        />
                                                    </FlexDiv>
                                                </>
                                            }
                                        </ApiConsumer>
                                    }
                                </FeaturesConsumer>
                            </Styled.TimerHeader>

                            <FlexDiv direction="column" flex={1}>
                                {selectedTimer.active &&
                                    <RunningTimerChunkItem
                                        timer={selectedTimer}
                                        onAddNote={() => {
                                            rootStore.appStore.setPreviousActiveElement();
                                            addTimerNote();
                                        }}
                                    />
                                }
                                <div style={{ flex: '1 1 auto' }}>
                                    <AutoSizer>
                                        {({ width, height }) => {
                                            return (
                                                <Styled.TimerChunkList>
                                                    <List
                                                        height={height}
                                                        rowHeight={(ind: Index) => 'isHeader' in sortedChunks[ind.index] ? 40 : 80}
                                                        rowCount={sortedChunks.length}
                                                        width={width}
                                                        onScroll={() => setUpdateChunkProps(true)}
                                                        rowRenderer={({ style, key, index }) => {
                                                            if ('isHeader' in sortedChunks[index]) {
                                                                const date = (sortedChunks[index] as TimerChunksHeader).date!;
                                                                const jsDate = new Date(date);
                                                                const dateObj = {
                                                                    year: jsDate.getFullYear(),
                                                                    month: jsDate.getMonth() + 1,
                                                                    day: jsDate.getDate()
                                                                };
                                                                const dateTime = DateTime.fromObject(dateObj);
                                                                return (
                                                                    <div key={key} style={style}>
                                                                        <Styled.TimerChunksHeader>
                                                                            <Styled.ListDate
                                                                                tabIndex={0}
                                                                                aria-label={`${dateTime.toFormat('EEEE')}, ${dateTime.toFormat(getDateFormat())}`}
                                                                                selected={false}
                                                                                onClick={() => selectAllForDay(date)}
                                                                                onKeyDown={(e) => handleEnterKey(e, () => selectAllForDay(date))}
                                                                            >
                                                                                {`${dateTime.toFormat('EEE')}, ${dateTime.toFormat(getDateFormat())}`}
                                                                            </Styled.ListDate>
                                                                        </Styled.TimerChunksHeader>
                                                                    </div>
                                                                );
                                                            }
                                                            const chunk = sortedChunks[index] as TimerChunk;
                                                            return (
                                                                <div key={chunk.id}>
                                                                    <TimerChunkItem
                                                                        timeEntryOpen={() =>
                                                                            openTimeEntry(
                                                                                chunk.timeEntryId!,
                                                                                selectedTimer.id || 0
                                                                            )
                                                                        }
                                                                        segmentChange={chunkEdit(index)}
                                                                        toggleSelect={(evt) => {toggleSelect(evt, chunk.id!)}}
                                                                        selected={selectedChunks.includes(chunk.id!)}
                                                                        onDelete={() => {
                                                                            rootStore.appStore.setPreviousActiveElement();
                                                                            deleteChunk(chunk);
                                                                        }}
                                                                        onEdit={editChunk(chunk)}
                                                                        segment={chunk}
                                                                        key={chunk.id}
                                                                        style={style}
                                                                        updateSegmentNarrative={updateSegmentNarrative}
                                                                        clearSelections={clearSelections}
                                                                        shouldUpdate={shouldUpdateChunkProps}
                                                                        setChunkPropsUpdate={setUpdateChunkProps}
                                                                    />
                                                                </div>
                                                            );
                                                        }}
                                                    />
                                                </Styled.TimerChunkList>
                                            );
                                        }}
                                    </AutoSizer>
                                </div>

                            </FlexDiv>
                            <FabContainer innerRef={this.props.fabRef} onScreen={selectedChunks.length > 0 || dirty}>
                                {dirty &&
                                    <>
                                        <Button
                                            buttonRef={this.props.fabButtonRef}
                                            onClick={saveTimer}
                                            variant="extendedFab"
                                            aria-label={t('selected_timer.fab_container.action.save')}
                                        >
                                            <Save />
                                            {t('selected_timer.fab_container.action.save.label')}
                                        </Button>
                                        <Button
                                            onClick={revert}
                                            variant="extendedFab"
                                            color="secondary"
                                            aria-label={t('selected_timer.fab_container.action.revert')}
                                        >
                                            <Undo />
                                            {t('selected_timer.fab_container.action.revert')}
                                        </Button>
                                    </>
                                }

                                {(selectedChunks.length > 0) &&
                                    <>
                                        <Button
                                            buttonRef={dirty ? null : this.props.fabButtonRef}
                                            onClick={clearSelections}
                                            variant="extendedFab"
                                            color="secondary"
                                            aria-label={t('clear', { ns: 'common' })}
                                        >
                                            {/*<Undo />*/}
                                            {t('selected_timer.fab_container.action.deselect')} ({selectedChunks.length})
                                        </Button>
                                        <Button
                                            onClick={entryFromSelectedChunks}
                                            variant="extendedFab"
                                            aria-label={t('selected_timer.fab_container.action.create_timeentry')}
                                        >
                                            <Check />
                                            {t('selected_timer.fab_container.action.create_timeentry')} ({selectedChunks.length})
                                        </Button>
                                        <Button
                                            onClick={openTransferDialog}
                                            variant="extendedFab"
                                            aria-label={t('selected_timer.fab_container.action.transfer')}
                                        >
                                            <CallMerge />
                                            {t('selected_timer.fab_container.action.transfer')} ({selectedChunks.length})
                                        </Button>
                                        <Button
                                            onClick={deleteSelectedChunks}
                                            variant="extendedFab"
                                            aria-label={t('delete', { ns: 'common' })}
                                        >
                                            <Delete />
                                            {t('delete', { ns: 'common' })} ({selectedChunks.length})
                                        </Button>
                                    </>
                                }
                            </FabContainer>
                        </>
                    )
                }}
            </TKConsumer>
        );
    }
}

export default withTranslation(['timers', 'common'])(TimerFormContainer);